import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Crown, ChartLineUp, Users, CurrencyDollar, ArrowRight, ArrowLeft,
  TrendUp, TrendDown, ArrowsClockwise, X, Receipt, Sparkle, Warning, CheckCircle
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const STATUS_COLOR = {
  active: 'bg-emerald-500 text-white',
  trialing: 'bg-amber-500 text-black',
  past_due: 'bg-red-500 text-white',
  canceled: 'bg-neutral-500 text-white',
  free: 'bg-neutral-300 text-neutral-800',
};

const EVENT_ICON = {
  trial_started: { icon: Sparkle, color: 'text-amber-500' },
  subscription_started: { icon: CheckCircle, color: 'text-emerald-500' },
  cancellation_scheduled: { icon: Warning, color: 'text-red-500' },
  reactivated: { icon: ArrowsClockwise, color: 'text-emerald-500' },
  subscription_canceled_at_period_end: { icon: X, color: 'text-neutral-500' },
  subscription_past_due: { icon: Warning, color: 'text-red-500' },
  renewal_reminder_t_minus_7: { icon: Receipt, color: 'text-blue-500' },
  renewal_reminder_t_minus_3: { icon: Receipt, color: 'text-blue-500' },
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'bg-[#D95800]', testid }) => (
  <div
    className="bg-white dark:bg-neutral-800 border-4 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-8 h-8 ${color} rounded-md flex items-center justify-center border-2 border-black`}>
        <Icon size={16} weight="bold" className="text-white" />
      </div>
      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{label}</p>
    </div>
    <p className="text-2xl font-black text-neutral-900 dark:text-white" data-testid={`${testid}-value`}>{value}</p>
    {sub && <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5">{sub}</p>}
  </div>
);

export default function AdminSubscriptions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [events, setEvents] = useState([]);
  const [reasons, setReasons] = useState(null);
  const [days, setDays] = useState(() => Number(searchParams.get('days')) || 30);
  const [loading, setLoading] = useState(true);

  // Admin-only route guard: redirect non-admin users away
  useEffect(() => {
    const userJson = localStorage.getItem('user');
    if (!userJson) return;
    try {
      const user = JSON.parse(userJson);
      if (user.user_type && user.user_type !== 'admin') {
        toast({ title: 'Admin access only', variant: 'destructive' });
        navigate(user.user_type === 'worker' ? '/worker-dashboard' : user.user_type === 'contractor' ? '/contractor-dashboard' : '/');
      }
    } catch {/* ignore */}
  }, [navigate]);

  const fetchAll = async (windowDays = days) => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [s, f, e, r] = await Promise.all([
        axios.get(`${API_URL}/api/admin/subscriptions/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/subscriptions/funnel?days=${windowDays}`, { headers }),
        axios.get(`${API_URL}/api/admin/subscriptions/events?limit=100`, { headers }),
        axios.get(`${API_URL}/api/admin/subscriptions/cancellation-reasons?days=${windowDays}`, { headers }),
      ]);
      setStats(s.data);
      setFunnel(f.data);
      setEvents(e.data?.events || []);
      setReasons(r.data);
    } catch (err) {
      toast({
        title: 'Failed to load',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional, re-runs only on days change
  useEffect(() => { fetchAll(days); }, [days]);

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F2] dark:bg-neutral-900">
        <ArrowsClockwise size={32} className="animate-spin text-[#D95800]" />
      </div>
    );
  }

  const totals = stats?.totals || {};
  const totalSubs = (totals.active || 0) + (totals.trialing || 0);
  const conv = stats?.conversion || {};
  const f = funnel?.funnel || {};
  const r = funnel?.rates || {};

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 py-8" data-testid="admin-subscriptions-page">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="h-9 w-9 bg-white dark:bg-neutral-800 border-2 border-black rounded-lg flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-neutral-700"
              data-testid="back-to-admin-btn"
            ><ArrowLeft size={16} weight="bold" /></button>
            <Crown size={28} weight="fill" className="text-[#D95800]" />
            <div>
              <h1 className="text-2xl font-black text-neutral-900 dark:text-white">Subscriptions</h1>
              <p className="text-xs text-neutral-500">Conversion funnel and revenue analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => {
                const v = Number(e.target.value);
                setDays(v);
                setSearchParams({ days: String(v) });
              }}
              className="h-10 px-3 bg-white dark:bg-neutral-800 border-2 border-black rounded-lg text-sm font-bold"
              data-testid="window-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            <button
              onClick={() => fetchAll(days)}
              className="h-10 px-3 bg-white dark:bg-neutral-800 border-2 border-black rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
              data-testid="refresh-btn"
            ><ArrowsClockwise size={14} weight="bold" /></button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={CurrencyDollar} label="MRR" value={`$${(stats?.mrr || 0).toLocaleString()}`} sub={`$${(stats?.annualized_revenue || 0).toLocaleString()} ARR`} color="bg-emerald-500" testid="kpi-mrr" />
          <KpiCard icon={Users} label="Paid Subscribers" value={stats?.paid_subscribers || 0} sub={`${totals.trialing || 0} on trial`} color="bg-[#2B4C6F]" testid="kpi-paid" />
          <KpiCard icon={ChartLineUp} label="Trial → Paid" value={`${conv.conversion_rate_pct || 0}%`} sub={`${conv.trials_converted || 0} of ${conv.trials_started || 0}`} color="bg-[#D95800]" testid="kpi-conversion" />
          <KpiCard icon={Sparkle} label="Active Subs" value={totalSubs} sub={`${totals.active || 0} active · ${totals.trialing || 0} trialing · ${totals.past_due || 0} past due`} color="bg-amber-500" testid="kpi-active" />
        </div>

        {/* Funnel + Plan Distribution Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Funnel */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]" data-testid="funnel-card">
            <h3 className="text-sm font-black uppercase tracking-wider text-neutral-700 dark:text-neutral-200 mb-4 flex items-center gap-2">
              <ChartLineUp size={16} weight="bold" /> Conversion Funnel — last {days} days
            </h3>
            {[
              { key: 'trial_started', label: 'Trials Started', color: 'bg-amber-500' },
              { key: 'subscription_started', label: 'Trials Converted to Paid', color: 'bg-emerald-500' },
              { key: 'cancellation_scheduled', label: 'Cancellations Scheduled', color: 'bg-red-400' },
              { key: 'reactivated', label: 'Saves (Reactivated)', color: 'bg-[#2B4C6F]' },
              { key: 'subscription_canceled_at_period_end', label: 'Confirmed Cancels', color: 'bg-neutral-500' },
              { key: 'subscription_past_due', label: 'Past Due (Failed Renewal)', color: 'bg-red-600' },
            ].map((row) => {
              const v = f[row.key] || 0;
              const max = Math.max(1, ...Object.values(f));
              const pct = (v / max) * 100;
              return (
                <div key={row.key} className="mb-2.5" data-testid={`funnel-row-${row.key}`}>
                  <div className="flex items-center justify-between mb-1 text-xs">
                    <span className="font-bold text-neutral-700 dark:text-neutral-300">{row.label}</span>
                    <span className="font-black text-neutral-900 dark:text-white">{v}</span>
                  </div>
                  <div className="w-full h-3 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden border border-black">
                    <div className={`h-full ${row.color} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="mt-4 grid grid-cols-2 gap-3 pt-3 border-t border-neutral-200 dark:border-neutral-700">
              <div>
                <p className="text-[10px] uppercase font-bold text-neutral-500">Trial → Paid</p>
                <p className="text-lg font-black text-emerald-600">{r.trial_to_paid_pct || 0}%</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-neutral-500">Save Rate</p>
                <p className="text-lg font-black text-[#2B4C6F]">{r.trial_save_pct || 0}%</p>
              </div>
            </div>
          </div>

          {/* Plan Distribution */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]" data-testid="plans-card">
            <h3 className="text-sm font-black uppercase tracking-wider text-neutral-700 dark:text-neutral-200 mb-4 flex items-center gap-2">
              <Crown size={16} weight="bold" /> Plan Distribution
            </h3>
            {(stats?.plan_distribution || []).length === 0 ? (
              <p className="text-sm text-neutral-500 italic">No paid subscriptions yet.</p>
            ) : (
              (stats?.plan_distribution || []).map((p) => (
                <div key={p.plan_id} className="mb-3" data-testid={`plan-${p.plan_id}`}>
                  <div className="flex items-center justify-between mb-1 text-xs">
                    <div>
                      <span className="font-black text-neutral-900 dark:text-white">{p.plan_id.replace(/_/g, ' ')}</span>
                      <span className="ml-2 text-neutral-500">${p.monthly_price}/mo</span>
                    </div>
                    <span className="font-bold text-neutral-700 dark:text-neutral-300">{p.count} sub{p.count !== 1 ? 's' : ''} · ${p.mrr_contribution}/mo</span>
                  </div>
                  <div className="w-full h-2.5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D95800]" style={{ width: `${Math.min(100, (p.count / Math.max(1, totalSubs)) * 100)}%` }} />
                  </div>
                </div>
              ))
            )}
            <div className="mt-4 pt-3 border-t border-neutral-200 dark:border-neutral-700 grid grid-cols-5 gap-2 text-center">
              {['active', 'trialing', 'past_due', 'canceled', 'free'].map((s) => (
                <div key={s} data-testid={`status-${s}`}>
                  <span className={`inline-block px-2 py-0.5 text-[9px] font-black rounded-full uppercase mb-1 border border-black ${STATUS_COLOR[s]}`}>{s.replace('_', ' ')}</span>
                  <p className="text-sm font-black text-neutral-900 dark:text-white">{totals[s] || 0}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cancellation Reasons Panel */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] mb-6" data-testid="reasons-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-black uppercase tracking-wider text-neutral-700 dark:text-neutral-200 flex items-center gap-2">
              <X size={16} weight="bold" /> Why People Cancel — last {days} days
            </h3>
            <span className="text-xs text-neutral-500 font-bold">
              {reasons?.total_with_reason || 0} of {reasons?.total_cancellations || 0} cancellations responded
              {reasons?.response_rate_pct ? ` · ${reasons.response_rate_pct}% response rate` : ''}
            </span>
          </div>
          {(reasons?.reasons || []).length === 0 ? (
            <p className="text-sm text-neutral-500 italic">No cancellations with reasons yet — micro-survey will populate this as users cancel.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                {(reasons?.reasons || []).map((r) => {
                  const max = Math.max(1, ...(reasons.reasons || []).map((x) => x.count));
                  const pct = (r.count / max) * 100;
                  return (
                    <div key={r.reason_code} className="mb-2.5" data-testid={`reason-row-${r.reason_code}`}>
                      <div className="flex items-center justify-between mb-1 text-xs">
                        <span className="font-bold text-neutral-700 dark:text-neutral-300">{r.label}</span>
                        <span className="font-black text-neutral-900 dark:text-white">{r.count}</span>
                      </div>
                      <div className="w-full h-3 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden border border-black">
                        <div className="h-full bg-red-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div>
                <p className="text-xs font-black uppercase text-neutral-500 mb-2">Recent feedback</p>
                {(reasons?.feedback_samples || []).length === 0 ? (
                  <p className="text-xs text-neutral-400 italic">No written feedback yet.</p>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {(reasons?.feedback_samples || []).slice(0, 8).map((f, i) => (
                      <div key={i} className="text-xs p-2 bg-neutral-50 dark:bg-neutral-900 rounded border border-neutral-200 dark:border-neutral-700" data-testid={`feedback-sample-${i}`}>
                        <p className="text-neutral-700 dark:text-neutral-300 italic">"{f.feedback}"</p>
                        <p className="text-[10px] text-neutral-500 mt-1">{f.reason_label || f.reason_code} · {f.plan_id}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Activity Stream */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]" data-testid="events-card">
          <h3 className="text-sm font-black uppercase tracking-wider text-neutral-700 dark:text-neutral-200 mb-4 flex items-center gap-2">
            <Receipt size={16} weight="bold" /> Recent Activity ({events.length})
          </h3>
          {events.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">No subscription events yet.</p>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-2">
              {events.map((ev, idx) => {
                const meta = EVENT_ICON[ev.event] || { icon: ArrowRight, color: 'text-neutral-500' };
                const Icon = meta.icon;
                return (
                  <div
                    key={ev._id}
                    className="flex items-center gap-3 p-2.5 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700"
                    data-testid={`event-row-${idx}`}
                  >
                    <Icon size={18} weight="bold" className={`${meta.color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-neutral-900 dark:text-white capitalize truncate">
                        {(ev.event || '').replace(/_/g, ' ')}
                        {ev.plan_id && <span className="ml-2 text-xs font-mono text-neutral-500">[{ev.plan_id}]</span>}
                      </p>
                      <p className="text-[11px] text-neutral-500 truncate">
                        {ev.user_name || 'Unknown'} · {ev.user_email || ev.user_id}
                      </p>
                    </div>
                    <span className="text-[10px] text-neutral-400 flex-shrink-0">{formatDate(ev.created_at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
